import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Shield, Eye, Database, Lock, Globe, Mail, Trash2, UserCheck } from 'lucide-react';

export default function PrivacyPolicy() {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const content = {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: January 2025",
      intro: "Ella Gold Official ('we', 'us', 'our') respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information in accordance with the General Data Protection Regulation (GDPR).",
      sections: [
        {
          icon: Database,
          title: "1. Data We Collect",
          content: `We collect the following types of personal data:

**Account Information:**
• Email address
• Name (optional)
• Password (encrypted)
• Profile preferences

**Transaction Data:**
• Purchase history
• Payment method (processed by Stripe - we don't store card details)
• Invoices and receipts

**Usage Data:**
• Pages visited
• Features used
• Device information (browser, OS)
• IP address (anonymized after 30 days)

**Communication Data:**
• Support tickets
• Chat messages with AI assistant`
        },
        {
          icon: Eye,
          title: "2. How We Use Your Data",
          content: `We process your personal data for the following purposes:

• **Contract Performance:** Processing purchases, delivering digital content, managing your account
• **Legal Obligations:** Tax records, fraud prevention, age verification
• **Legitimate Interests:** Improving our services, security monitoring, analytics
• **Consent:** Marketing communications (only with your explicit consent)

We never sell your personal data to third parties.`
        },
        {
          icon: Lock,
          title: "3. Data Security",
          content: `We implement robust security measures to protect your data:

• SSL/TLS encryption for all data transmission
• Passwords are hashed using bcrypt (industry standard)
• Payment processing handled by PCI-compliant Stripe
• Regular security audits and updates
• Access controls limiting employee data access
• Secure MongoDB database with encryption at rest`
        },
        {
          icon: Globe,
          title: "4. Data Sharing",
          content: `We share your data only with:

**Payment Processor (Stripe):**
• Processes payments securely
• Subject to their own Privacy Policy

**Cloud Infrastructure:**
• Hosted on secure servers
• Data processed within EU/EEA

**Legal Requirements:**
• When required by law or court order
• To protect our rights or prevent fraud

We do NOT share data with advertisers or marketing companies.`
        },
        {
          icon: UserCheck,
          title: "5. Your Rights (GDPR)",
          content: `Under GDPR, you have the following rights:

• **Access:** Request a copy of your personal data
• **Rectification:** Correct inaccurate or incomplete data
• **Erasure:** Request deletion of your data ("right to be forgotten")
• **Portability:** Receive your data in a machine-readable format
• **Restriction:** Limit how we process your data
• **Objection:** Object to certain processing activities
• **Withdraw Consent:** Revoke consent for marketing at any time

To exercise these rights, contact us at privacy@ellagold.com`
        },
        {
          icon: Database,
          title: "6. Data Retention",
          content: `We retain your data for:

• **Account Data:** Until account deletion + 30 days
• **Transaction Records:** 7 years (legal requirement)
• **Usage Logs:** 90 days (anonymized thereafter)
• **Support Communications:** 2 years

After these periods, data is securely deleted or anonymized.`
        },
        {
          icon: Shield,
          title: "7. Cookies",
          content: `We use the following cookies:

**Essential Cookies:**
• Authentication tokens
• Language preferences
• Age verification status

**Analytics Cookies (with consent):**
• Anonymous usage statistics
• Performance monitoring

You can manage cookie preferences through our cookie banner or browser settings.`
        },
        {
          icon: Mail,
          title: "8. Contact & Complaints",
          content: `**Data Controller:**
Ella Gold
Torenallee 101, 5617BR Eindhoven
The Netherlands
KVK: 99681293

**Contact:**
Email: privacy@ellagold.com

**Supervisory Authority:**
If you're unsatisfied with our response, you may lodge a complaint with:
Autoriteit Persoonsgegevens (Dutch Data Protection Authority)
www.autoriteitpersoonsgegevens.nl`
        }
      ]
    },
    nl: {
      title: "Privacybeleid",
      lastUpdated: "Laatst bijgewerkt: Januari 2025",
      intro: "Ella Gold Official ('wij', 'ons', 'onze') respecteert uw privacy en zet zich in voor de bescherming van uw persoonsgegevens. Dit Privacybeleid legt uit hoe wij uw informatie verzamelen, gebruiken en beschermen in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG).",
      sections: [
        {
          icon: Database,
          title: "1. Gegevens die we verzamelen",
          content: `Wij verzamelen de volgende soorten persoonsgegevens:

**Accountinformatie:**
• E-mailadres
• Naam (optioneel)
• Wachtwoord (versleuteld)
• Profielvoorkeuren

**Transactiegegevens:**
• Aankoopgeschiedenis
• Betaalmethode (verwerkt door Stripe - wij slaan geen kaartgegevens op)
• Facturen en bonnen

**Gebruiksgegevens:**
• Bezochte pagina's
• Gebruikte functies
• Apparaatinformatie (browser, OS)
• IP-adres (geanonimiseerd na 30 dagen)

**Communicatiegegevens:**
• Supporttickets
• Chatberichten met AI-assistent`
        },
        {
          icon: Eye,
          title: "2. Hoe we uw gegevens gebruiken",
          content: `Wij verwerken uw persoonsgegevens voor de volgende doeleinden:

• **Contractuitvoering:** Verwerken van aankopen, leveren van digitale content, beheren van uw account
• **Wettelijke verplichtingen:** Belastingadministratie, fraudepreventie, leeftijdsverificatie
• **Gerechtvaardigde belangen:** Verbeteren van onze diensten, beveiligingsmonitoring, analyses
• **Toestemming:** Marketingcommunicatie (alleen met uw expliciete toestemming)

Wij verkopen uw persoonsgegevens nooit aan derden.`
        },
        {
          icon: Lock,
          title: "3. Gegevensbeveiliging",
          content: `Wij implementeren robuuste beveiligingsmaatregelen om uw gegevens te beschermen:

• SSL/TLS-versleuteling voor alle gegevensoverdracht
• Wachtwoorden worden gehasht met bcrypt (industriestandaard)
• Betalingsverwerking door PCI-conforme Stripe
• Regelmatige beveiligingsaudits en updates
• Toegangscontroles die werknemerstoegang tot gegevens beperken
• Beveiligde MongoDB-database met versleuteling`
        },
        {
          icon: Globe,
          title: "4. Gegevens delen",
          content: `Wij delen uw gegevens alleen met:

**Betalingsverwerker (Stripe):**
• Verwerkt betalingen veilig
• Onderworpen aan hun eigen Privacybeleid

**Cloud-infrastructuur:**
• Gehost op beveiligde servers
• Gegevensverwerking binnen EU/EER

**Wettelijke vereisten:**
• Wanneer vereist door wet of gerechtelijk bevel
• Om onze rechten te beschermen of fraude te voorkomen

Wij delen GEEN gegevens met adverteerders of marketingbedrijven.`
        },
        {
          icon: UserCheck,
          title: "5. Uw rechten (AVG)",
          content: `Onder de AVG heeft u de volgende rechten:

• **Inzage:** Een kopie van uw persoonsgegevens opvragen
• **Rectificatie:** Onjuiste of onvolledige gegevens corrigeren
• **Wissen:** Verwijdering van uw gegevens verzoeken ("recht om vergeten te worden")
• **Overdraagbaarheid:** Uw gegevens ontvangen in een machine-leesbaar formaat
• **Beperking:** Beperken hoe wij uw gegevens verwerken
• **Bezwaar:** Bezwaar maken tegen bepaalde verwerkingsactiviteiten
• **Toestemming intrekken:** Toestemming voor marketing op elk moment intrekken

Om deze rechten uit te oefenen, neem contact met ons op via privacy@ellagold.com`
        },
        {
          icon: Database,
          title: "6. Bewaartermijnen",
          content: `Wij bewaren uw gegevens voor:

• **Accountgegevens:** Tot accountverwijdering + 30 dagen
• **Transactierecords:** 7 jaar (wettelijke vereiste)
• **Gebruikslogboeken:** 90 dagen (daarna geanonimiseerd)
• **Supportcommunicatie:** 2 jaar

Na deze periodes worden gegevens veilig verwijderd of geanonimiseerd.`
        },
        {
          icon: Shield,
          title: "7. Cookies",
          content: `Wij gebruiken de volgende cookies:

**Essentiële cookies:**
• Authenticatietokens
• Taalvoorkeuren
• Leeftijdsverificatiestatus

**Analytische cookies (met toestemming):**
• Anonieme gebruiksstatistieken
• Prestatiemonitoring

U kunt cookievoorkeuren beheren via onze cookiebanner of browserinstellingen.`
        },
        {
          icon: Mail,
          title: "8. Contact & Klachten",
          content: `**Verwerkingsverantwoordelijke:**
Ella Gold
Torenallee 101, 5617BR Eindhoven
Nederland
KVK: 99681293

**Contact:**
E-mail: privacy@ellagold.com

**Toezichthouder:**
Als u ontevreden bent over onze reactie, kunt u een klacht indienen bij:
Autoriteit Persoonsgegevens
www.autoriteitpersoonsgegevens.nl`
        }
      ]
    },
    de: {
      title: "Datenschutzerklärung",
      lastUpdated: "Zuletzt aktualisiert: Januar 2025",
      intro: "Ella Gold Official respektiert Ihre Privatsphäre und verpflichtet sich zum Schutz Ihrer personenbezogenen Daten gemäß der DSGVO.",
      sections: [
        { icon: Database, title: "1. Daten, die wir erheben", content: "Wir erheben Kontodaten, Transaktionsdaten und Nutzungsdaten, um unsere Dienste bereitzustellen." },
        { icon: Eye, title: "2. Verwendung Ihrer Daten", content: "Wir verwenden Ihre Daten zur Vertragserfüllung, rechtlichen Verpflichtungen und zur Verbesserung unserer Dienste." },
        { icon: UserCheck, title: "3. Ihre Rechte", content: "Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Datenübertragbarkeit." }
      ]
    },
    fr: {
      title: "Politique de Confidentialité",
      lastUpdated: "Dernière mise à jour: Janvier 2025",
      intro: "Ella Gold Official respecte votre vie privée et s'engage à protéger vos données personnelles conformément au RGPD.",
      sections: [
        { icon: Database, title: "1. Données collectées", content: "Nous collectons des données de compte, de transaction et d'utilisation pour fournir nos services." },
        { icon: Eye, title: "2. Utilisation des données", content: "Nous utilisons vos données pour l'exécution du contrat, les obligations légales et l'amélioration de nos services." }
      ]
    },
    es: {
      title: "Política de Privacidad",
      lastUpdated: "Última actualización: Enero 2025",
      intro: "Ella Gold Official respeta su privacidad y se compromete a proteger sus datos personales de acuerdo con el RGPD.",
      sections: [
        { icon: Database, title: "1. Datos recopilados", content: "Recopilamos datos de cuenta, transacciones y uso para proporcionar nuestros servicios." }
      ]
    },
    it: {
      title: "Informativa sulla Privacy",
      lastUpdated: "Ultimo aggiornamento: Gennaio 2025",
      intro: "Ella Gold Official rispetta la tua privacy e si impegna a proteggere i tuoi dati personali in conformità con il GDPR.",
      sections: [
        { icon: Database, title: "1. Dati raccolti", content: "Raccogliamo dati dell'account, transazioni e utilizzo per fornire i nostri servizi." }
      ]
    },
    ru: {
      title: "Политика конфиденциальности",
      lastUpdated: "Последнее обновление: Январь 2025",
      intro: "Ella Gold Official уважает вашу конфиденциальность и обязуется защищать ваши персональные данные в соответствии с GDPR.",
      sections: [
        { icon: Database, title: "1. Собираемые данные", content: "Мы собираем данные учетной записи, транзакций и использования для предоставления наших услуг." }
      ]
    },
    ar: {
      title: "سياسة الخصوصية",
      lastUpdated: "آخر تحديث: يناير 2025",
      intro: "تحترم Ella Gold Official خصوصيتك وتلتزم بحماية بياناتك الشخصية وفقًا للائحة العامة لحماية البيانات.",
      sections: [
        { icon: Database, title: "1. البيانات المجمعة", content: "نجمع بيانات الحساب والمعاملات والاستخدام لتقديم خدماتنا." }
      ]
    }
  };

  const currentContent = content[lang] || content.en;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="privacy-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
            <Shield className="w-10 h-10 text-[#D4AF37]" />
            {currentContent.title}
          </h1>
          <p className="text-gray-400 mb-6">{currentContent.lastUpdated}</p>
          <p className="text-gray-300 max-w-2xl mx-auto">{currentContent.intro}</p>
        </div>

        <div className="space-y-8">
          {currentContent.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <div 
                key={idx}
                className="p-6 bg-white/5 rounded-xl border border-white/10"
              >
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                  {section.title}
                </h2>
                <div className="text-gray-300 whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 p-6 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30 text-center">
          <p className="text-gray-300 mb-2">Data Protection Contact:</p>
          <a href="mailto:privacy@ellagold.com" className="text-[#D4AF37] hover:underline font-medium">
            privacy@ellagold.com
          </a>
        </div>
      </div>
    </div>
  );
}
